import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import logo from '../images/logo.svg';
import logoDark from '../images/logo-dark.svg';
import loginGraphic from '../images/login-tiles.png'; 
import googleIcon from '../images/googleicon.png'; 
import facebookIcon from '../images/facebook.png'; 
import passwordView from '../images/password-view.svg'; 
import passwordHide from '../images/hidepassword.svg'; 
import { useUser } from '../redux/useUser';
import { showSuccessToast, showErrorToast } from "../components/Toast";
import { webAuth } from '../utils/auth0'; // Import webAuth from utils
import countryCodes from '../utils/countryCodes.json';
import { api, getAuthHeaders } from "../utils/api";
import CountrySelectReact from '../utils/CountrySelect';
import { detectUserLocale } from '../utils/localeDetection';
import { X } from "lucide-react";

// Name utils (CommonJS export from src/utils/nameUtils.js)
const { sanitizeName, validateName } = require('../utils/nameUtils');

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [showMsgModal, setShowMsgModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [step, setStep] = useState('initial');
  const [accessToken, setAccessToken] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const layersRef = useRef([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [activeTab, setActiveTab] = useState('email'); // 'email' or 'phone'
  const [showSignup, setShowSignup] = useState(false); // New state for controlling signup view
  const [signupTouched, setSignupTouched] = useState({ name: false, email: false, password: false });
  const [signupNameError, setSignupNameError] = useState('');
  const [loginTouched, setLoginTouched] = useState({ email: false, password: false });
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const navigate = useNavigate();
  const [code, setCode] = useState('+1');
  // 🔹 MFA state
  const [mfaToken, setMfaToken] = useState('');
  const [mfaOtp, setMfaOtp] = useState('');

  // OTP Modal states and refs
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const otpRefs = useRef([]);

  const maskPhoneNumber = (phone, dialCode) => {
    if (!phone) return "";
    const len = phone.length;
    if (len <= 4) {
      return `${dialCode} ${phone}`;
    }
    const firstTwo = phone.slice(0, 2);
    const lastFour = phone.slice(-4);
    const maskedLength = Math.max(0, len - 6);
    const masked = "X".repeat(maskedLength);
    return `${dialCode} ${firstTwo}${masked}${lastFour}`;
  };

  // Email validation helper (simple RFC-like check)
  const isValidEmail = (v) => {
    if (!v || typeof v !== 'string') return false;
    // Basic email regex: local@domain.tld (allows +, -, _ in local)
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v.trim());
  };

  // E.164-like phone validation: + followed by 10-15 digits
  const isValidE164 = (v) => {
    if (!v || typeof v !== 'string') return false;
    return /^\+[0-9]{10,15}$/.test(v.trim());
  };

  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      const savedTheme = localStorage.getItem("theme");
      setIsDark(savedTheme === "dark");
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // 🔹 Detect user locale on mount and set country code
  useEffect(() => {
    const storedCurrency = localStorage.getItem('user_currency');
    const storedCountry = localStorage.getItem('user_country');
    const storedDialCode = localStorage.getItem('user_dial_code');
    
    if (!storedCurrency || !storedCountry || !storedDialCode) {
      // Detect locale for first-time users
      const localeInfo = detectUserLocale();
      console.log('🌍 Detected Locale Info:', localeInfo);
      
      // Store detected values
      localStorage.setItem('user_currency', localeInfo.currency);
      localStorage.setItem('user_country', localeInfo.countryCode);
      localStorage.setItem('user_dial_code', localeInfo.dialCode);
      
      // Set country code for phone login
      setCode(localeInfo.dialCode);
      setCountryCode(localeInfo.dialCode);
    } else {
      // Use stored values for returning users
      setCode(storedDialCode);
      setCountryCode(storedDialCode);
    }
  }, []);

  // verify email UI states
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verifyEmailMessage, setVerifyEmailMessage] = useState("");
  const [resendLoadings, setResendLoadings] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining


  // Redux user hook
  const { user, loading: userLoading, error: userError, getUser } = useUser();

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState("");
  const [resendLoading, setResendLoading] = useState(false); // for OTP resend

  // OTP expiry timer in seconds (3 minutes)
  const [resendTimer, setResendTimer] = useState(0);
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Auto-focus first input when OTP Modal is shown
  useEffect(() => {
    if (showOtpModal) {
      setTimeout(() => {
        if (otpRefs.current[0]) {
          otpRefs.current[0].focus();
        }
      }, 100);
    }
  }, [showOtpModal]);

  useEffect(() => {
    const getRandom = (min, max) => Math.random() * (max - min) + min;
    const animateLayer = (layer, duration) => {
      gsap.fromTo(
        layer,
        {
          x: getRandom(-400, 400),
          y: getRandom(-400, 400),
          scale: getRandom(1.1, 1.6),
          opacity: getRandom(0.4, 0.7),
          rotation: getRandom(-20, 20),
        },
        {
          x: getRandom(-400, 400),
          y: getRandom(-400, 400),
          scale: getRandom(1.3, 1.8),
          opacity: getRandom(0.6, 0.9),
          rotation: getRandom(-20, 20),
          duration,
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut",
        }
      );
    };
    layersRef.current.forEach((layer, index) => {
      const colors = [
        `radial-gradient(circle, rgba(255, 50, 150, 0.4), transparent 70%)`,
        `radial-gradient(circle, rgba(50, 150, 255, 0.4), transparent 70%)`,
        `radial-gradient(circle, rgba(150, 50, 255, 0.4), transparent 70%)`,
      ];
      layer.style.background = colors[index % colors.length];
      animateLayer(layer, getRandom(4, 6));
    });
  }, []);

  useEffect(() => {
    gsap.to(".img-loop", {
      x: 20,
      y: 20,
      duration: 3,
      ease: "power1.inOut",
      yoyo: true,
      repeat: -1,
    });
    gsap.to(".img-loop", {
      x: -20,
      y: -20,
      duration: 3,
      delay: 1.5,
      ease: "power1.inOut",
      yoyo: true,
      repeat: -1,
    });
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    const error = sessionStorage.getItem('login_error');
    if (error) {
      showErrorToast(error);
      sessionStorage.removeItem('login_error');
    }
  }, []);

  useEffect(() => {
    if (window.location.hash.includes('access_token')) {
      // handled in CallbackPage — do not duplicate here
    } else {
      // Try restoring from localStorage
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user_info');
      if (storedToken && storedUser) {
        setAccessToken(storedToken);
        setUserInfo(JSON.parse(storedUser));
        setStep('authenticated');
      }
    }
  }, []);

  // Shared post-login check: call /users/me with token then navigate or show verification UI
  const handlePostLogin = async (token) => {
    if (!token) return;
    try {
      const response = await api.get('/users/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update user currency from localStorage ONLY on first login
      const storedCurrency = localStorage.getItem('user_currency');
      const storedCountry = localStorage.getItem('user_country');
      const userData = response.data;
      
      // Check if this is first login: user doesn't have currency set OR currency is 'NA'
      const isFirstLogin = localStorage.getItem('currencySetDone') !== userData.id;
      
      if (isFirstLogin && storedCurrency && userData.id) {
        localStorage.setItem('detectedCurrency', storedCurrency);
        localStorage.setItem('currencySetDone', userData.id);
        try {
          await api.patch(`/users/${userData.id}`, {
            currency: storedCurrency,
            region: storedCurrency,
            address: {
              ...userData.address,
              country: storedCountry || userData.address?.country
            }
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          console.log('✅ First login: Currency updated to backend:', storedCurrency);
          
          // Update redux user state
          try {
            await getUser(token);
          } catch (e) {
            // ignore if getUser fails but API gave 200
          }
          // Navigate to dashboard immediately
          navigate('/dashboard');
          return;
        } catch (err) {
          console.warn('Failed to update currency on first login:', err);
        }
      } else {
        console.log('ℹ️ Subsequent login: Currency already set, skipping backend update');
      }
      
      // success — update redux and navigate
      try {
        await getUser(token);
      } catch (e) {
        // ignore if getUser fails but API gave 200
      }
      setShowVerifyEmail(false);
      setVerifyEmailMessage("");
      navigate('/dashboard');
    } catch (apiErr) {
      const status = apiErr?.response?.status;
      const message = (apiErr?.response?.data?.message || apiErr?.message || '').toString().toLowerCase();

      // If backend says "email not verified" -> show in-page verify box
      const needsVerification = status === 403 && message.includes('email');
      if (needsVerification) {
        setShowVerifyEmail(true);
        const storedUserInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
        const userEmail = storedUserInfo?.email || email;
        setVerifyEmailMessage(`A verification link was sent to ${userEmail}. Please verify your email before accessing the dashboard.`);
        // keep token stored so user can click "Resend" without re-login
        localStorage.setItem('access_token', token);
        return;
      }

      // Otherwise show error
      setError(apiErr?.response?.data?.message || apiErr.message || 'API call failed');
    }
  };

  // login function (email/password) - using client.login for non-redirect flow
  const loginWithEmailPassword = () => {

    if (!email) {
      setError('');
      return;
    }
    if (!password) {
      setError('');
      return;
    }

    // Validate email format
    if (!isValidEmail(email)) {
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    
    // Use client.login for direct token retrieval without redirect
    // This ensures fresh authentication validation against current password
    webAuth.client.login({
      realm: 'Username-Password-Authentication',
      username: email,
      password: password,
      scope: 'openid profile email phone',
    }, (err, authResult) => {
      setLoading(false);
      if (err) {
        // 🔹 Detect MFA required
        if (err.code === 'mfa_required') {
          try {
            let responseBody;
            if (err.original?.response?.text) {
              responseBody = JSON.parse(err.original.response.text);
            } else if (err.original?.message) {
              responseBody = JSON.parse(err.original.message);
            }
            
            if (responseBody?.mfa_token) {
              setMfaToken(responseBody.mfa_token);
              setStep('mfa-otp');
              setError('');
              return;
            }
          } catch (e) {
            console.error('Failed to parse MFA response:', e);
          }
        }
        setError(err.error_description || err.description || 'Login failed');
      } else if (authResult && authResult.accessToken) {
        // Success - store tokens and handle post-login
        localStorage.setItem('access_token', authResult.accessToken);
        if (authResult.idToken) {
          localStorage.setItem('id_token', authResult.idToken);
        }
        setAccessToken(authResult.accessToken);
        handlePostLogin(authResult.accessToken);
      } else {
        setError('Login succeeded but no token received');
      }
    });
  };

  // 🔹 MFA OTP verification
  const verifyMfaOtp = async () => {
    if (!mfaOtp || mfaOtp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://${process.env.REACT_APP_AUTH0_DOMAIN}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'http://auth0.com/oauth/grant-type/mfa-otp',
          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          mfa_token: mfaToken,
          otp: mfaOtp,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error_description || data.error || 'MFA verification failed');
      }

      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        if (data.id_token) {
          localStorage.setItem('id_token', data.id_token);
        }
        setAccessToken(data.access_token);
        setStep('authenticated');
        await handlePostLogin(data.access_token);
      } else {
        setError('MFA verification succeeded but no token received');
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err.message || 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  // signup function (email/password)
  const signupWithEmailPassword = () => {
    // validate email format first
    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    webAuth.signup({
      connection: 'Username-Password-Authentication',
      email,
      password,
      name, 
      user_metadata: { name }
    }, (err) => {
      setLoading(false);
      if (err) {
        // Handle duplicate email error for all providers
        // Check for invalid_signup error with statusCode 400 (from API)
        if (err.code === 'invalid_signup' || err.code === 'user_exists' || (err.description && err.description.toLowerCase().includes('user already exists'))) {
          setError('An account with this email already exists. Please log in or use a different email address.');
        } else {
          setError(err.description || 'Signup failed');
        }
      }
      else {
        // After signup, login and then call getUser with token
        webAuth.login({
          realm: 'Username-Password-Authentication',
          username: email,
          password,
        }, async (err) => {
          setLoading(false);
          if (err) setError(err.description || 'Login failed');
          else {
            const token = localStorage.getItem('access_token');
            if (token) {
              await handlePostLogin(token);
            } else {
              setError('Signup succeeded but token not found. Please check the callback flow.');
            }
          }
        });
      }
    });
  };

  // Enhanced phone number formatting with validation
  const formatPhoneNumber = (phone, code = countryCode) => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // If already starts with +, return as is
    if (phone.startsWith('+')) {
      return phone;
    }
    
    // Handle different country codes
    if (code === '+91') {
      // India: should be 10 digits after country code
      if (cleanPhone.length === 10) {
        return code + cleanPhone;
      }
    } else if (code === '+1') {
      // US/Canada: remove leading 1 if present to avoid +1 1XXXXXXXXXX
      if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
        return code + cleanPhone.substring(1);
      }
      // Ensure we have exactly 10 digits for US numbers
      if (cleanPhone.length === 10) {
        return code + cleanPhone;
      }
    } else if (code === '+44') {
      // UK: can be 10-11 digits after country code
      if (cleanPhone.length >= 10 && cleanPhone.length <= 11) {
        return code + cleanPhone;
      }
    }
    
    // Default: just append country code
    return code + cleanPhone;
  };

  // Handle OTP focus and entries
  const handleOtpChange = (value, idx) => {
    const cleanVal = value.replace(/\D/g, "");
    const newValues = [...otpValues];
    newValues[idx] = cleanVal.slice(-1); // store only the last digit typed
    setOtpValues(newValues);
    setOtpError("");

    // If entered a digit, move focus to the next input
    if (cleanVal && idx < 5) {
      if (otpRefs.current[idx + 1]) {
        otpRefs.current[idx + 1].focus();
      }
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newValues = [...otpValues];
      if (!otpValues[idx] && idx > 0) {
        // Current box is empty, clear previous box and focus it
        newValues[idx - 1] = "";
        setOtpValues(newValues);
        if (otpRefs.current[idx - 1]) {
          otpRefs.current[idx - 1].focus();
        }
      } else {
        // Clear current box
        newValues[idx] = "";
        setOtpValues(newValues);
      }
      setOtpError("");
    } else if (e.key === "Enter") {
      e.preventDefault();
      const fullOtp = otpValues.join('');
      if (fullOtp.length === 6 && !loading) {
        handleVerifyOtp();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pastedData) return;

    const newValues = [...otpValues];
    for (let i = 0; i < 6; i++) {
      newValues[i] = pastedData[i] || "";
    }
    setOtpValues(newValues);
    setOtpError("");

    // Focus last pasted element or the next empty element
    const nextFocusIdx = Math.min(pastedData.length, 5);
    if (otpRefs.current[nextFocusIdx]) {
      otpRefs.current[nextFocusIdx].focus();
    }
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError("");
    setResendTimer(0);
  };

  const sendOtp = () => {
    setPhoneTouched(true);
    if (!phoneNumber) {
      setPhoneError('Phone number is required');
      return;
    }

    const formattedPhone = formatPhoneNumber(phoneNumber, code);
    if (!isValidE164(formattedPhone)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    setPhoneError('');

    webAuth.passwordlessStart({
      connection: 'sms',
      phoneNumber: formattedPhone,
      send: 'code'
    }, (err) => {
      setLoading(false);
      if (err) {
        const errorMsg = err.description || 'Failed to send SMS';
        setError(errorMsg.includes('phone_number is not valid') ? 'Please enter a valid phone number or correct dial code.' : errorMsg);
      } else {
        setError('');
        setOtpValues(["", "", "", "", "", ""]);
        setOtpError("");
        setShowOtpModal(true);
        setResendTimer(180);
      }
    });
  };

  const handleVerifyOtp = () => {
    const fullOtp = otpValues.join('');
    if (fullOtp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setOtpError('');
    const formattedPhone = formatPhoneNumber(phoneNumber, code);

    webAuth.passwordlessLogin({
      connection: 'sms',
      phoneNumber: formattedPhone,
      verificationCode: fullOtp
    }, async (err, authResult) => {
      setLoading(false);
      if (err) {
        setOtpError(err.description || 'OTP verification failed');
      } else {
        const token = authResult?.accessToken;
        if (token) {
          localStorage.setItem('access_token', token);
          setAccessToken(token);
          setShowOtpModal(false);
          await handlePostLogin(token);
        } else {
          setOtpError('OTP verified but no token returned.');
        }
      }
    });
  };

  const loginWithSocial = (provider) => {
    const state = JSON.stringify({ returnTo: '/dashboard' });
    webAuth.authorize({
      connection: provider,
      redirectUri: window.location.origin + '/callback',
      responseType: 'token id_token',
      scope: 'openid profile email phone',
      state,
        // prompt: 'none',
    });
  };

  const handleResendOtp = () => {
    if (resendTimer > 0) return; // Prevent multiple requests
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError('');
    setResendLoading(true);
    setPhoneTouched(true);
    if (!phoneNumber) {
      setResendLoading(false);
      setPhoneError('Phone number is required');
      return;
    }
    const formattedPhone = formatPhoneNumber(phoneNumber, code);
    if (!isValidE164(formattedPhone)) {
      setResendLoading(false);
      setPhoneError('Please enter a valid phone number');
      return;
    }

    webAuth.passwordlessStart({
      connection: 'sms',
      phoneNumber: formattedPhone,
      send: 'code'
    }, (err) => {
      setResendLoading(false);
      if (err) {
        const errorMsg = err.description || 'Failed to send SMS';
        setOtpError(errorMsg.includes('phone_number is not valid') ? 'Please enter a valid phone number or correct dial code.' : errorMsg);
      } else {
        setOtpError('');
        setResendTimer(180);
        showSuccessToast('New OTP verification code sent!');
      }
    });
  };

  const sendResetLink = (customEmail) => {
    const useEmail = customEmail || email;
    if (!useEmail) {
      setError('Please enter your email to reset your password.');
      return;
    }
    if (!isValidEmail(useEmail)) {
      setForgotEmailError('Please enter a valid email address');
      return;
    }
    setForgotPasswordLoading(true);
    // Clear error state and sessionStorage errors before attempting reset
    setError('');
    sessionStorage.removeItem('login_error');
    webAuth.changePassword(
      {
        connection: 'Username-Password-Authentication',
        email: useEmail,
      },
      function (err, resp) {
        setForgotPasswordLoading(false);
        if (err) {
          console.error('Reset error:', err);
          setError('Failed to send reset email: ' + err.description);
        } else {
          // Success: clear error state and any stale auth data
          setError('');
          setMsg('We’ve received your request. If an account exists for this email, you’ll receive a password reset link within the next few minutes.');
          setShowMsgModal(true);
          // Comprehensive cleanup to ensure fresh authentication across browsers
          // Clear all localStorage items (collect keys first to avoid iteration issues)
          const localStorageKeys = [...Object.keys(localStorage)];
          localStorageKeys.forEach(key => {
            // Keep theme preference if needed, clear all auth-related data
            if (key !== 'theme') {
              localStorage.removeItem(key);
            }
          });
          // Clear all sessionStorage items (collect keys first to avoid iteration issues)
          const sessionStorageKeys = [...Object.keys(sessionStorage)];
          sessionStorageKeys.forEach(key => {
            sessionStorage.removeItem(key);
          });
        }
      }
    );
  };

  const handleForgotSubmit = () => {
    if (!forgotEmail) {
      setForgotEmailError("Email is required");
      return;
    }
    setForgotEmailError("");
    setShowForgotModal(false);
    setEmail(forgotEmail); // set main email state
    sendResetLink(forgotEmail);
  };

  // Prevent multiple getUser API calls: only call if token exists, user is not present, not loading, and userInfo is not present
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && !user && !userLoading && !userInfo) {
      getUser(token);
    }
  }, [getUser, user, userLoading, userInfo]);

  // resend verification email (calls backend)
const handleResendVerification = async () => {
  // prevent clicking while cooldown active
  if (resendCooldown > 0) return;

  // start cooldown immediately to avoid double clicks (you can also start on success instead)
  setResendCooldown(50);

  setResendLoadings(true);
  setError('');
  try {
    const stored = JSON.parse(localStorage.getItem('user_info') || '{}');
    const userEmail = stored?.email || email;
    if (!userEmail) {
      setError('Email not available to resend verification.');
      setResendLoadings(false);
      setResendCooldown(0); // rollback cooldown if we abort
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Session expired. Please log in again.');
      setResendLoadings(false);
      setResendCooldown(0);
      return;
    }

    await api.post('/users/me/email/resend-verification', {}, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setVerifyEmailMessage('Verification email resent! Please check your inbox.');
    showSuccessToast('Verification email resent!');
    // If you want to ensure cooldown remains even if request is slow, do nothing here.
    // If you started cooldown only on click and want to extend/ensure it after success, you could:
    // setResendCooldown(50);
  } catch (e) {
    console.error('Resend verification failed', e);
    // rollback cooldown on failure so user can try again sooner
    setResendCooldown(0);

    if (e?.response?.status === 401) {
      setError('Session invalid. Please log in again.');
    } else {
      setError(e?.response?.data?.message || e.message || 'Failed to resend verification email.');
    }
  } finally {
    setResendLoadings(false);
  }
};


  // show pending verification if callback set it
useEffect(() => {
  try {
    const pendingRaw = localStorage.getItem("pending_verification");
    if (pendingRaw) {
      const pending = JSON.parse(pendingRaw);
      // Only show the verify screen shortly after the rejected sign-in. Without this the flag never
      // clears and every later visit to /login stays stuck on "Email Verification Required".
      const PENDING_VERIFICATION_TTL_MS = 15 * 60 * 1000; // 15 minutes
      if (!pending?.ts || Date.now() - pending.ts > PENDING_VERIFICATION_TTL_MS) {
        localStorage.removeItem('pending_verification');
        localStorage.removeItem('access_token'); // token from the rejected sign-in
        return;
      }
      const userEmail = pending?.email || email || (JSON.parse(localStorage.getItem('user_info') || '{}')?.email);
      setShowVerifyEmail(true);
      setVerifyEmailMessage(pending?.msg || `A verification link was sent to ${userEmail}. Please verify your email.`);
      // keep token stored (CallbackPage previously stored it)
      // optionally remove the pending flag now (or keep it until user resends/clears)
      // localStorage.removeItem('pending_verification');
    }
  } catch (e) {
    // ignore parse errors
  }
}, []); // run once on mount

useEffect(() => {
  if (resendCooldown <= 0) return;
  const id = setInterval(() => {
    setResendCooldown((s) => {
      if (s <= 1) {
        clearInterval(id);
        return 0;
      }
      return s - 1;
    });
  }, 1000);
  return () => clearInterval(id);
}, [resendCooldown]);

const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;


  return (
    <>
      {/* Popup Modal for msg */}
      {showMsgModal && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.3)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 16px',boxSizing:'border-box'}}>
          <div style={{background:'#fff',padding:32,borderRadius:12,minWidth:300,maxWidth:'100%',boxShadow:'0 2px 16px rgba(0,0,0,0.15)',textAlign:'center',position:'relative',boxSizing:'border-box'}}>
            <div style={{fontSize:20,fontWeight:600,marginBottom:12}}>Password Reset</div>
            <div style={{marginBottom:20}}>{msg}</div>
            <button className="btn btn-primary" onClick={()=>setShowMsgModal(false)}>OK</button>
          </div>
        </div>
      )}
      <Container fluid className="login-container">
        <Row className="align-items-center">
          {/* Left Section */}
          <div className="authLeft text-center d-flex align-items-center justify-content-center">
            <img src={loginGraphic} alt="Login Graphic" className="img-loop" width="80%" />
          </div>

          {/* Right Section (Login Form) */}
          <Col className="auth-right">
            <div className="login-form" style={{ maxWidth: 480, position: 'relative' }}>
              <div className="brand-name">
                <Link to="/" className="header-logo text-decoration-none d-inline-block mb-3"><img src={isDark ? logoDark : logo} alt="logo"/></Link>  
              </div>

              {/* Show verification box in-place */}
              {showVerifyEmail ? (
                <div>
                  <h3>Email Verification Required</h3>
                  <p className="my-8">{verifyEmailMessage}</p>
                  {error && (
                    <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mb-2">
                      {error}
                    </div>
                  )}
                  <div style={{marginTop:12}}>
                    <button
  className="px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors w-full"
  onClick={handleResendVerification}
  disabled={resendLoadings || resendCooldown > 0}
>
  {resendLoadings ? 'Sending...' : (resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email')}
</button>

                    <div style={{marginTop:12, textAlign:'center'}}>
                    <button className="text-primary-blue fs-14 fw-semibold" onClick={() => {
                      localStorage.removeItem('pending_verification');
                      localStorage.removeItem('access_token');
                      // allow user to go back to login form and retry
                      setShowVerifyEmail(false);
                      setVerifyEmailMessage('');
                      // optionally remove pending token so login attempts are clean
                      // localStorage.removeItem('access_token');
                    }}>
                      Back to Login
                    </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {showForgotModal
                    ? <h3>Forgot password</h3>
                    : (showSignup ? <h3>Create new account</h3> : <h3>Login to your account</h3>)
                  }
                  <p className="mb-4">Securely access your account and manage your assets with ease.</p>
                  {error && (
                    <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mb-2">
                      {error}
                    </div>
                  )}

                  {!showSignup && !showForgotModal &&( 
                    <div className="d-flex mb-4 gap-2">
                      <Button variant={activeTab === 'email' ? 'solid-tab' : 'outline-tab'} className="flex-1" onClick={() => setActiveTab('email')}>Login with Email</Button>
                      <Button variant={activeTab === 'phone' ? 'solid-tab' : 'outline-tab'} className="flex-1" onClick={() => { setActiveTab('phone'); setError(''); }}>Login with Phone </Button>
                    </div>
                  )}

                  {/* Email/Password Login Section */}
                  {activeTab === 'email' && (
                    <div className="space-y-3">
                      {!showForgotModal ? (
                        <>
                          {/* 🔹 MFA OTP Section */}
                          {step === 'mfa-otp' ? (
                            <div className="space-y-3 mt-3">
                              <p className="fs-14 fw-normal text-gray-600 mb-3">
                                Multi-factor authentication is enabled. Enter the 6-digit code from your authenticator app.
                              </p>
                              <Form.Group controlId="mfa-otp" className="form-floating mb-3">
                                <Form.Control
                                  type="text"
                                  placeholder="Enter 6-digit code"
                                  value={mfaOtp}
                                  onChange={e => setMfaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  maxLength="6"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      verifyMfaOtp();
                                    }
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: '2px solid #e0e0e0',
                                    borderRadius: 0,
                                    boxShadow: 'none',
                                    fontSize: 18,
                                    paddingLeft: 0,
                                    paddingRight: 40,
                                    color: '#222',
                                    textAlign: 'center',
                                    letterSpacing: '0.5em'
                                  }}
                                  autoComplete="one-time-code"
                                  autoFocus
                                />
                                <Form.Label>MFA Code</Form.Label>
                              </Form.Group>
                              <button
                                onClick={verifyMfaOtp}
                                disabled={loading || mfaOtp.length !== 6}
                                className="px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors w-full"
                              >
                                {loading ? 'Verifying...' : 'Verify Code'}
                              </button>
                              <button
                                onClick={() => {
                                  setStep('initial');
                                  setMfaOtp('');
                                  setMfaToken('');
                                  setError('');
                                }}
                                className="w-full text-sm text-primary-blue fw-medium py-2"
                              >
                                Back to Login
                              </button>
                            </div>
                          ) : (
                            <>
                          {/* Show name field only for Sign Up */}
                          {showSignup && (
                            <Form.Group controlId="name" className="form-floating mb-3">
                              <Form.Control
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={e => {
                                  // strip digits while typing and clear previous error
                                  let val = e.target.value.replace(/\d+/g, '');
                                  const normalized = val.length === 1 && val[0] === ' ' ? '' : val;
                                  setName(normalized);
                                  setSignupTouched(t => ({...t, name: true}));
                                  setSignupNameError('');
                                }}
                                onBlur={() => {
                                  // remove any digits (defense), sanitize and validate on blur
                                  let cleaned = name.replace(/\d+/g, '');
                                  cleaned = sanitizeName(cleaned);
                                  setName(cleaned);
                                  setSignupTouched(t => ({...t, name: true}));
                                  const res = validateName(cleaned, { minLength: 2, maxLength: 50 });
                                  setSignupNameError(res.valid ? '' : res.message);
                                }}
                                maxLength={50}
style={{background: 'transparent', border: 'none', borderBottom: '2px solid #e0e0e0', borderRadius: 0, boxShadow: 'none', fontSize: 18, paddingLeft: 0, paddingRight: 40}} 
                                autoComplete="name"
                                isInvalid={!!signupNameError || (signupTouched.name && !name)}
                              />
                              <Form.Label>Name</Form.Label>
                              {signupNameError && <div className="text-danger small mt-1">{signupNameError}</div>}
                            </Form.Group>
                          )}
                          <Form.Group controlId="email" className="form-floating mb-4">
                            <Form.Control
                              type="email"
                              placeholder="Email"
                              value={email}
                                onChange={e => { setEmail(e.target.value); if (showSignup) setSignupTouched(t => ({...t, email: true})); }}
                                onBlur={() => showSignup && setSignupTouched(t => ({...t, email: true}))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (!showSignup) {
                                      setLoginTouched({ email: true, password: true });
                                      loginWithEmailPassword();
                                    }
                                  }
                                }}
style={{background: 'transparent', border: 'none', borderBottom: '2px solid #e0e0e0', borderRadius: 0, boxShadow: 'none', fontSize: 18, paddingLeft: 0, paddingRight: 40}}
                              className=""
                              autoComplete="username"
                                isInvalid={
                                  (showSignup && signupTouched.email && !isValidEmail(email)) ||
                                  (!showSignup && loginTouched.email && (!email || !isValidEmail(email)))
                                }
                            />
                            <Form.Label>Email</Form.Label>
                                {showSignup && signupTouched.email && !email && <div className="text-danger small mt-1">Email is required</div>}
                                {showSignup && signupTouched.email && email && !isValidEmail(email) && <div className="text-danger small mt-1">Please enter a valid email address</div>}
                                {!showSignup && loginTouched.email && !email && <div className="text-danger small mt-1">Email is required</div>}
                                {!showSignup && loginTouched.email && email && !isValidEmail(email) && <div className="text-danger small mt-1">Please enter a valid email address</div>}
                          </Form.Group>
                         

<Form.Group controlId="password" className="form-floating mb-0 position-relative">
  <Form.Control
    type={showPassword ? "text" : "password"}
    placeholder="Password"
    value={password}
    onChange={e => { 
      const val = e.target.value;
      setPassword(val); 
      if (showSignup) setSignupTouched(t => ({ ...t, password: true }));
      // Clear top-level password errors as user types a value or when it becomes valid
      if (val) {
        const lower = (error || '').toLowerCase();
        if (lower.includes('password')) setError('');
      }
    }}
    onBlur={() => showSignup && setSignupTouched(t => ({ ...t, password: true }))}
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!showSignup) {
          setLoginTouched({ email: true, password: true });
          loginWithEmailPassword();
        }
      }
    }}
    style={{
      background: 'transparent',
      border: 'none',
      borderBottom: '2px solid #e0e0e0',
      borderRadius: 0,
      boxShadow: 'none',
      fontSize: 18,
      paddingLeft: 0,
      paddingRight: 40
    }}
    autoComplete="current-password"
    isInvalid={
      (showSignup && signupTouched.password && (!password || !passwordRegex.test(password))) ||
      (!showSignup && loginTouched.password && !password)
    }
  />

  <Form.Label>Password</Form.Label>

  <span
    className="eye-icon"
    style={{ position:'absolute', right:10, top:'30px', cursor:'pointer', zIndex:2, height:20, display:'inline-flex', alignItems:'center', justifyContent:'center' }}
    onClick={togglePasswordVisibility}
  >
    <img
      src={showPassword ? passwordView : passwordHide}
      alt={showPassword ? "Hide password" : "Show password"}
      width="28"
      style={{ opacity: 0.6 }}
    />
  </span>

  {/* Required Message */}
  {showSignup && signupTouched.password && !password && (
    <div className="text-danger small mt-1">Password is required</div>
  )}

  {!showSignup && loginTouched.password && !password && (
    <div className="text-danger small mt-1">Password is required</div>
  )}

  {/* Invalid Password Format */}
  {showSignup && signupTouched.password && password && !passwordRegex.test(password) && (
    <div className="text-danger small mt-1">
      Password must be at least 8 characters long and contain at least one special character.
    </div>
  )}
</Form.Group>

                          <div className="flex gap-2 mt-0 ">
                            {showSignup ? ( 
                              <button 
                                onClick={e => {
                                  setSignupTouched({ name: true, email: true, password: true });
                                  const cleaned = sanitizeName(name);
                                  setName(cleaned);
                                  const res = validateName(cleaned, { minLength: 2, maxLength: 50 });
                                  if (!res.valid) {
                                    setSignupNameError(res.message);
                                    return;
                                  }
                                  if (!name || !email || !password) return;
                                  signupWithEmailPassword();
                                }} 
                                disabled={loading}
                                className="mt-4 px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors w-full"
                              >
                                {loading ? 'Sign Up...' : 'Sign Up'}
                              </button>
                            ) : (
                              <div className="flex flex-col items-end w-full">                     
                                <button
                                  type="button"
                                  className="btn p-0 text-primary-blue border-0 mb-3 mt-1 fw-medium fs-14"
                                  onClick={()=>setShowForgotModal(true)}
                                  style={{position:'relative', minWidth:120}}
                                >
                                  Forgot Password?
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => { setLoginTouched({ email: true, password: true }); loginWithEmailPassword(); }} 
                                  disabled={loading}
                                  className="px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors w-full"
                                >
                                  {loading ? 'Login...' : 'Login'}
                                </button>
                              </div>
                            )}
                          </div>
                            </>
                          )}
                        </>
                      ) : (
                          <div style={{marginTop:10}}>
                          <Form.Group controlId="forgot-email" className="form-floating mb-3">
                            <Form.Control
                              type="email"
                              placeholder="Enter your email"
                              value={forgotEmail}
                              onChange={e => { setForgotEmail(e.target.value); setForgotEmailError(""); }}
                              style={{background: 'transparent', border: 'none', borderBottom: '2px solid #e0e0e0', borderRadius: 0, boxShadow: 'none', fontSize: 18, paddingLeft: 0, paddingRight: 40, color: '#222'}}
                              autoFocus
                              isInvalid={!!forgotEmailError}
                            />
                            <Form.Label>Enter your email</Form.Label>
                            {forgotEmailError && <div className="text-danger small mt-1">{forgotEmailError}</div>}
                          </Form.Group>
                          <div className="d-flex gap-2">
                            <button className="px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors flex-1" onClick={handleForgotSubmit} disabled={forgotPasswordLoading}>
                              {forgotPasswordLoading ? 'Sending...' : 'Reset Password'}
                            </button>
                            <button className="flex-1 fw-medium rounded-full border-solid border-1 border-gray-300 hover:border-blue-600 text-gray-400 hover:text-blue-600 hover:bg-blue-50" onClick={()=>{setShowForgotModal(false); setForgotEmailError("");}}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                                   {/* Phone/OTP Login Section */}
                  {activeTab === 'phone' && (
                    <div className="space-y-3 mt-3">
                      <Form.Group controlId="phone" className="form-floating mb-3 d-flex align-items-end gap-2">
                        <div style={{paddingTop:20}}>
                          <CountrySelectReact value={code} onChange={setCode} maxWidth={130} />
                        </div>
                        <Form.Control
                          type="tel"
                          placeholder="Phone Number"
                          value={phoneNumber}
                          onChange={e => {
                            const input = e.target.value.replace(/\D/g, '').slice(0, 15);
                            setPhoneNumber(input);
                            setPhoneTouched(false);
                            setPhoneError('');
                          }}
                          maxLength={15}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setPhoneTouched(true);
                              sendOtp();
                            }
                          }}
                          style={{background: 'transparent', border: 'none', borderBottom: '2px solid #e0e0e0', borderRadius: 0, boxShadow: 'none', fontSize: 18, paddingLeft: 0, paddingRight: 40, color: '#222'}}
                          className="flex-1"
                        />
                      </Form.Group>
                      {phoneTouched && phoneError && (
                        <div className="text-danger small mt-1">{phoneError}</div>
                      )}
                      {phoneTouched && !phoneError && (phoneNumber.length < 10 || phoneNumber.length > 15) && (
                        <div className="text-danger small mt-1">Please enter between 10 and 15 digits</div>
                      )}
                      <p className="fs-14 fw-normal text-gray-500 mt-1 lh-base">
                        We'll send you a verification code.
                      </p>
                      
                      <button 
                        onClick={() => { setPhoneTouched(true); sendOtp(); }} 
                        disabled={loading}
                        className="px-4 py-2 bg-primary-blue text-white font-medium rounded-full transition-colors w-full"
                      >
                        {loading ? 'Processing...' : 'Send OTP'}
                      </button>
                    </div>
                  )}

                  <p className="text-center or-login my-4"><span className="fs-14">Or login with</span> </p>
                  <div className="social-buttons">
                    <Button variant="none" className="btn-social btn-outline-primary d-flex align-items-center justify-content-center py-2"   onClick={() => loginWithSocial('google-oauth2')} >
                      <img src={googleIcon} alt="Google Login" width="24" /> Google
                    </Button>
                    <Button variant="none" className="btn-social btn-outline-primary d-flex align-items-center justify-content-center py-2"  onClick={() => loginWithSocial('facebook')} >
                      <img src={facebookIcon} alt="Facebook Login" width="22" /> Facebook
                    </Button>
                  </div>

                  {!showForgotModal && (
                    <p className="text-center mt-4">
                      {!showSignup ? (
                        <>
                          <span className="fs-14 fw-medium">Not a member? </span>
                          <button 
                            onClick={() => { setShowSignup(true); setActiveTab('email'); setError(''); }}
                            className="text-primary-blue fs-14 fw-semibold"
                          >
                            Sign Up
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="fs-14 fw-medium">Already have an account? </span>
                          <button 
                            onClick={() => { setShowSignup(false); setError(''); }}
                            className="text-primary-blue fs-14 fw-semibold"
                          >
                            Login
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </>
              )}
              {showOtpModal && (
                <div className="otp-modal-overlay">
                  <div className="otp-modal-content">
                    <button className="otp-modal-close" onClick={handleCloseOtpModal} aria-label="Close modal">
                      <X size={20} />
                    </button>
                    <h3 className="otp-modal-title">Verification Code</h3>
                    <p className="otp-modal-subtitle">
                      We've sent a 6-digit verification code to your phone number:<br />
                      <strong className="masked-phone">{maskPhoneNumber(phoneNumber, code)}</strong>
                    </p>

                    {otpError && (
                      <div className="text-danger p-2 bg-red-50 border border-red-200 rounded mb-3 small text-center">
                        {otpError}
                      </div>
                    )}

                    <div className="otp-input-container">
                      {otpValues.map((value, idx) => (
                        <input
                          key={idx}
                          ref={(el) => (otpRefs.current[idx] = el)}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={value}
                          onChange={(e) => handleOtpChange(e.target.value, idx)}
                          onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                          onPaste={handleOtpPaste}
                          className="otp-digit-input"
                          disabled={loading}
                          autoComplete="one-time-code"
                        />
                      ))}
                    </div>

                    <div className="otp-timer-resend-row">
                      {resendTimer > 0 ? (
                        <span className="otp-timer-text">
                          OTP expires in <strong className="timer-countdown">{formatTimer(resendTimer)}</strong>
                        </span>
                      ) : (
                        <span className="otp-timer-expired">OTP expired</span>
                      )}
                    </div>

                    <button
                      onClick={handleVerifyOtp}
                      disabled={loading || otpValues.some(val => !val)}
                      className="otp-verify-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </button>

                    <div className="otp-actions-footer">
                      <button
                        onClick={handleResendOtp}
                        disabled={resendLoading || resendTimer > 0}
                        className="otp-resend-btn"
                      >
                        {resendLoading ? 'Sending...' : 'Resend OTP'}
                      </button>
                      <div className="divider-vertical" />
                      <button
                        onClick={handleCloseOtpModal}
                        className="otp-change-phone-btn"
                      >
                        Change Phone Number
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default LoginPage;
